import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import type { AgentActivityStatus, UserActivityStatus } from '../utils'

interface ActivityStatusIndicatorProps {
  readonly status: AgentActivityStatus | UserActivityStatus
  readonly isCompacting?: boolean
  readonly isInterrupt?: boolean
}

export const ActivityStatusIndicator: FC<ActivityStatusIndicatorProps> = ({
  status,
  isCompacting = false,
  isInterrupt = false,
}) => {
  const theme = useTheme()

  const getColor = (): string => {
    switch (status) {
      case 'pre_tool':
        return theme.palette.primary.main
      case 'post_tool':
        return theme.palette.success.main
      case 'system':
        return theme.palette.mode === 'dark' ? '#00bcd4' : theme.palette.info.main
      case 'user':
        return theme.palette.warning.main
      case 'idle':
      default:
        return theme.palette.text.disabled
    }
  }

  const shouldPulse = (status === 'user' && !isInterrupt) || (status === 'system' && isCompacting)

  return (
    <Box
      component="span"
      sx={shouldPulse ? pulsingIndicatorSx(getColor()) : staticIndicatorSx(getColor())}
    />
  )
}

const baseIndicatorSx: SxProps<Theme> = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
}

const staticIndicatorSx = (color: string): SxProps<Theme> => ({
  ...baseIndicatorSx,
  backgroundColor: color,
})

const pulsingIndicatorSx = (color: string): SxProps<Theme> => ({
  ...baseIndicatorSx,
  backgroundColor: color,
  animation: 'indicatorPulse 1.5s ease-in-out infinite',
  '@keyframes indicatorPulse': {
    '0%, 100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.6,
      transform: 'scale(1.2)',
    },
  },
})
